<template>
  <v-container fluid>
    <v-row align="center" justify="space-between" class="mb-4">
      <v-col cols="12" md="8">
        <h2 class="text-h5 font-weight-bold">Nuevo presupuesto</h2>
      </v-col>
      <v-col cols="12" md="4" class="text-md-right">
        <v-btn text color="primary" @click="$router.back()">
          <v-icon left>mdi-arrow-left</v-icon>
          Volver
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <!-- Panel izquierdo -->
      <v-col cols="12" md="4">
        <v-card class="mb-4">
          <v-card-title class="subtitle-1 font-weight-bold">Cliente</v-card-title>
          <v-card-text>
            <v-autocomplete
              v-model="clienteSeleccionado"
              :items="clientesSearch"
              :loading="loadingClientes"
              :search-input.sync="searchCliente"
              item-text="nombre_completo"
              item-value="id"
              outlined
              dense
              label="Buscar cliente..."
              placeholder="Nombre o apellido"
              no-filter
              clearable
              hide-no-data
              return-object
            />
          </v-card-text>
        </v-card>

        <v-card class="mb-4">
          <v-card-title class="subtitle-1 font-weight-bold">Opciones</v-card-title>
          <v-card-text>
            <v-text-field
              v-model="descuento"
              type="number"
              min="0"
              max="100"
              label="Descuento (%)"
              outlined
              dense
              suffix="%"
            />
            <v-text-field
              v-model="fechaVencimiento"
              type="date"
              label="Vencimiento"
              outlined
              dense
            />
          </v-card-text>
        </v-card>

        <v-card>
          <v-card-title class="subtitle-1 font-weight-bold">Observaciones</v-card-title>
          <v-card-text>
            <v-textarea
              v-model="observaciones"
              outlined
              dense
              rows="3"
              placeholder="Opcional"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Panel derecho: detalle -->
      <v-col cols="12" md="8">
        <v-card>
          <v-card-title class="subtitle-1 font-weight-bold">
            Detalle
            <v-spacer />
            <span v-if="detalles.length" class="caption grey--text mr-3">{{ detalles.length }} ítem(s)</span>
            <PresupuestoDesdeArchivo v-if="puedeUsarAsistente" @aplicar="aplicarLectura" />
            <v-btn small color="primary" @click="dialogProducto = true">
              <v-icon left small>mdi-plus</v-icon>
              Agregar producto
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-alert v-if="pendientesArchivo.length" type="warning" dense text class="text-caption">
              {{ pendientesArchivo.length }} línea(s) del archivo sin producto, agregalas a mano:
              <span v-for="(l, i) in pendientesArchivo" :key="i"><b>{{ l.descripcion }}</b> ({{ l.cantidad }}){{ i < pendientesArchivo.length - 1 ? ", " : "" }}</span>
            </v-alert>
            <v-simple-table v-if="detalles.length > 0">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Descripción</th>
                    <th class="text-right" style="width: 130px">Cantidad</th>
                    <th style="width: 50px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(d, i) in detalles" :key="i">
                    <td>{{ d.descripcion }}</td>
                    <td class="text-right">
                      <v-text-field
                        v-model.number="d.cantidad"
                        type="number"
                        min="0.01"
                        dense
                        outlined
                        hide-details
                        style="width: 100px"
                        class="ml-auto"
                      />
                    </td>
                    <td>
                      <v-btn icon small color="error" @click="quitarDetalle(i)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <v-alert v-else type="info" outlined dense>
              Usá el botón "Agregar producto" para sumar ítems al presupuesto.
            </v-alert>
          </v-card-text>

          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn
              color="success"
              :disabled="!puedeGuardar"
              :loading="loadingGuardar"
              @click="registrar"
            >
              <v-icon left>mdi-content-save</v-icon>
              Guardar presupuesto
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog selector de producto -->
    <v-dialog v-if="dialogProducto" v-model="dialogProducto" persistent width="1000px">
      <v-card class="rounded-lg">
        <v-card-title class="headline">
          <v-icon left>mdi-package-variant-closed</v-icon>
          Seleccionar producto
          <v-spacer />
          <v-btn icon @click="dialogProducto = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-container class="pa-4">
          <ProductoSelector
            :showSuggestions="false"
            @process-producto="agregarProducto"
            @close-dialog="dialogProducto = false"
          />
        </v-container>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ProductoSelector from '@/components/shared/comprobantes/detalles/ProductoSelector.vue';
import PresupuestoDesdeArchivo from '@/components/asistente/PresupuestoDesdeArchivo.vue';

export default {
  name: 'PresupuestoEmpresaRegistrar',
  components: { ProductoSelector, PresupuestoDesdeArchivo },
  data() {
    return {
      clienteSeleccionado: null,
      clientesSearch: [],
      searchCliente: '',
      loadingClientes: false,
      detalles: [],
      descuento: '',
      fechaVencimiento: '',
      observaciones: '',
      loadingGuardar: false,
      dialogProducto: false,
      pendientesArchivo: [],
      _debounceCliente: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['puedeUsarAsistente']),
    puedeGuardar() {
      return this.clienteSeleccionado && this.detalles.length > 0;
    },
  },
  watch: {
    searchCliente(val) {
      if (!val || val.length < 2) { this.clientesSearch = []; return; }
      clearTimeout(this._debounceCliente);
      this._debounceCliente = setTimeout(() => this.fetchClientes(val), 300);
    },
  },
  methods: {
    ...mapActions('presupuestoVenta', ['create_presupuesto']),

    async fetchClientes(query) {
      this.loadingClientes = true;
      try {
        const q = query.trim().replace(/\s+/g, '-');
        const res = await this.axios.get(`/clientes/search?q=${encodeURIComponent(q)}`);
        this.clientesSearch = (res.data || []).map(c => ({
          ...c,
          nombre_completo: `${c.nombre} ${c.apellido}`,
        }));
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingClientes = false;
      }
    },

    agregarProducto(producto) {
      const existente = this.detalles.find(d => d.psp === producto.id);
      if (existente) {
        existente.cantidad = parseFloat(existente.cantidad) + 1;
      } else {
        this.detalles.push({ psp: producto.id, descripcion: producto.descripcion, cantidad: 1 });
      }
    },

    // Lo leído del archivo se suma a lo que ya estaba cargado; el cliente solo
    // se completa si el archivo lo nombraba y se encontró en el sistema.
    aplicarLectura({ cliente, detalles, pendientes }) {
      if (cliente) {
        this.clientesSearch = [cliente];
        this.clienteSeleccionado = cliente;
      }
      detalles.forEach((d) => {
        const existente = this.detalles.find((x) => x.psp === d.psp);
        if (existente) existente.cantidad = parseFloat(existente.cantidad) + d.cantidad;
        else this.detalles.push(d);
      });
      this.pendientesArchivo = pendientes;
    },

    quitarDetalle(index) {
      this.detalles.splice(index, 1);
    },

    async registrar() {
      this.loadingGuardar = true;
      try {
        const payload = {
          cliente: this.clienteSeleccionado.id,
          observaciones: this.observaciones,
          detalles: this.detalles.map(d => ({ psp: d.psp, cantidad: String(d.cantidad) })),
        };
        if (this.descuento) payload.descuento = String(this.descuento);
        if (this.fechaVencimiento) payload.fecha_vencimiento = this.fechaVencimiento;

        const res = await this.create_presupuesto(payload);
        this.$updateBus.$emit('showSnackBar', {
          show: true, button: false, color: 'success',
          text: 'Presupuesto creado correctamente', icon: 'mdi-check', timeout: 2500,
        });
        this.$router.push({ name: 'empresa.ventas.presupuesto.consultar', params: { id: res.data.id } });
      } catch (e) {
        const msg = e.response?.data?.error || 'Error al registrar el presupuesto';
        this.$updateBus.$emit('showSnackBar', {
          show: true, button: false, color: 'error',
          text: msg, icon: 'mdi-alert', timeout: 3500,
        });
      } finally {
        this.loadingGuardar = false;
      }
    },
  },
};
</script>
